/**
 * ANALYTICS TRACKER - ChatbotImóveis
 * Sistema completo de rastreamento para captura de leads
 *
 * Captura:
 * - UTM parameters
 * - Sessão e comportamento
 * - Dispositivo e navegador
 * - Interação com formulário
 * - Histórico de visitas
 * - Consentimento LGPD
 */

// ============================================
// TYPES
// ============================================

interface PageVisit {
  path: string;
  timestamp: string;
}

interface TrackingData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_page: string | null;
  first_touch_point: string | null;
  last_cta_clicked: string | null;
}

interface SessionData {
  time_on_site: number;
  time_on_page: number;
  pages_visited: number;
  pages_visited_list: string[];
  scroll_depth: number;
  returning_visitor: boolean;
  previous_visits: number;
  first_visit_date: string | null;
  session_id: string | null;
  visitor_id: string | null;
}

interface DeviceData {
  device_type: string;
  device_os: string;
  browser: string;
  browser_version: string;
  browser_language: string;
  screen_resolution: string;
  timezone: string;
}

interface FormError {
  field: string;
  message: string;
}

interface FormData {
  time_to_fill: number | null;
  fields_filled: number;
  fields_corrected: string[];
  autocomplete_used: boolean;
  form_abandonment_count: number;
  submit_attempts: number;
  form_errors: FormError[];
}

interface ConsentData {
  lgpd_consent: boolean;
  lgpd_consent_type: string;
  lgpd_consent_timestamp: string;
  privacy_policy_version: string;
  marketing_consent: boolean;
}

export interface AllTrackingData {
  trackingData: TrackingData;
  sessionData: SessionData;
  deviceData: DeviceData;
  formData: FormData | null;
  consentData: ConsentData;
}

// ============================================
// INICIALIZAÇÃO
// ============================================

/**
 * Inicializa o tracking quando o usuário entra no site
 * Deve ser chamado no App.tsx ou index.tsx
 */
export function initTracking(): void {
  // 1. Visitor ID persistente (único por navegador)
  if (!localStorage.getItem('visitor_id')) {
    localStorage.setItem('visitor_id', generateId('vis'));
    localStorage.setItem('first_visit_date', new Date().toISOString());
    localStorage.setItem('visit_count', '1');
  } else {
    // Incrementar contador de visitas
    const count = parseInt(localStorage.getItem('visit_count') || '1');
    localStorage.setItem('visit_count', (count + 1).toString());
    localStorage.setItem('last_visit', new Date().toISOString());
  }

  // 2. Session ID (expira ao fechar navegador)
  if (!sessionStorage.getItem('session_id')) {
    sessionStorage.setItem('session_id', generateId('sess'));
    sessionStorage.setItem('session_start', Date.now().toString());
    sessionStorage.setItem('pages_visited_count', '0');
    sessionStorage.setItem('pages_visited', JSON.stringify([]));
  }

  // 3. Landing page (primeira página da sessão)
  if (!sessionStorage.getItem('landing_page')) {
    sessionStorage.setItem('landing_page', window.location.pathname);
    sessionStorage.setItem('first_touch_point', window.location.pathname);
  }

  // 4. UTM parameters (persistir na sessão)
  const params = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
    const value = params.get(key);
    if (value && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, value);
    }
  });

  // 5. Referrer (apenas na primeira página)
  if (!sessionStorage.getItem('referrer') && document.referrer) {
    sessionStorage.setItem('referrer', document.referrer);
  }

  // 6. Track página atual
  trackPageView(window.location.pathname);

  // 7. Inicializar tracking de scroll
  initScrollTracking();

  // 8. Inicializar tracking de tempo
  initTimeTracking();

  console.log('✅ Analytics Tracker inicializado');
}

// ============================================
// TRACKING DE PÁGINAS
// ============================================

export function trackPageView(pathname: string): void {
  // Incrementar contador
  const count = parseInt(sessionStorage.getItem('pages_visited_count') || '0') + 1;
  sessionStorage.setItem('pages_visited_count', count.toString());

  // Adicionar à lista
  const pages: PageVisit[] = JSON.parse(sessionStorage.getItem('pages_visited') || '[]');
  pages.push({
    path: pathname,
    timestamp: new Date().toISOString()
  });
  sessionStorage.setItem('pages_visited', JSON.stringify(pages));

  // Atualizar tempo na página anterior
  const lastPageTime = sessionStorage.getItem('current_page_start');
  if (lastPageTime) {
    const timeOnPage = Math.floor((Date.now() - parseInt(lastPageTime)) / 1000);
    sessionStorage.setItem('last_page_time', timeOnPage.toString());
  }

  // Iniciar tempo na página atual
  sessionStorage.setItem('current_page_start', Date.now().toString());
}

// ============================================
// TRACKING DE SCROLL
// ============================================

let maxScroll = 0;
let scrollTimeout: ReturnType<typeof setTimeout> | undefined;

function initScrollTracking(): void {
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollHeight > 0
        ? Math.floor((window.scrollY / scrollHeight) * 100)
        : 100;

      maxScroll = Math.max(maxScroll, scrollPercent);
      sessionStorage.setItem('max_scroll', maxScroll.toString());
    }, 100);
  });
}

export function getScrollDepth(): number {
  return parseInt(sessionStorage.getItem('max_scroll') || '0');
}

// ============================================
// TRACKING DE TEMPO
// ============================================

function initTimeTracking(): void {
  // Atualizar tempo no site periodicamente
  setInterval(() => {
    const sessionStart = parseInt(sessionStorage.getItem('session_start') || Date.now().toString());
    const timeOnSite = Math.floor((Date.now() - sessionStart) / 1000);
    sessionStorage.setItem('time_on_site', timeOnSite.toString());
  }, 5000); // A cada 5 segundos
}

export function getTimeOnSite(): number {
  const sessionStart = parseInt(sessionStorage.getItem('session_start') || Date.now().toString());
  return Math.floor((Date.now() - sessionStart) / 1000);
}

export function getTimeOnPage(): number {
  const pageStart = parseInt(sessionStorage.getItem('current_page_start') || Date.now().toString());
  return Math.floor((Date.now() - pageStart) / 1000);
}

// ============================================
// TRACKING DE CTA
// ============================================

export function trackCTAClick(ctaId: string, ctaLabel: string): void {
  sessionStorage.setItem('last_cta_clicked', ctaId);
  sessionStorage.setItem('last_cta_label', ctaLabel);
  sessionStorage.setItem('last_cta_timestamp', new Date().toISOString());

  console.log('📊 CTA clicado:', ctaId, ctaLabel);
}

// ============================================
// TRACKING DE FORMULÁRIO
// ============================================

export class FormTracker {
  private formId: string;
  private startTime: number | null;
  private endTime: number | null;
  private fieldsInteracted: Set<string>;
  private fieldsCorrected: Set<string>;
  private abandonmentCount: number;
  private submitAttempts: number;
  private autocompleteUsed: boolean;
  private errors: FormError[];

  constructor(formId: string) {
    this.formId = formId;
    this.startTime = null;
    this.endTime = null;
    this.fieldsInteracted = new Set();
    this.fieldsCorrected = new Set();
    this.abandonmentCount = 0;
    this.submitAttempts = 0;
    this.autocompleteUsed = false;
    this.errors = [];
  }

  start(): void {
    this.startTime = Date.now();
    console.log('📝 Form tracking started:', this.formId);
  }

  trackFieldInteraction(fieldName: string): void {
    this.fieldsInteracted.add(fieldName);
  }

  trackFieldCorrection(fieldName: string): void {
    this.fieldsCorrected.add(fieldName);
  }

  trackAutocomplete(): void {
    this.autocompleteUsed = true;
  }

  trackError(fieldName: string, errorMessage: string): void {
    this.errors.push({ field: fieldName, message: errorMessage });
  }

  trackAbandonment(): void {
    this.abandonmentCount++;
  }

  trackSubmitAttempt(): void {
    this.submitAttempts++;
  }

  complete(): FormData {
    this.endTime = Date.now();
    return this.getData();
  }

  getData(): FormData {
    const timeToFill = this.endTime && this.startTime
      ? Math.floor((this.endTime - this.startTime) / 1000)
      : null;

    return {
      time_to_fill: timeToFill,
      fields_filled: this.fieldsInteracted.size,
      fields_corrected: Array.from(this.fieldsCorrected),
      autocomplete_used: this.autocompleteUsed,
      form_abandonment_count: this.abandonmentCount,
      submit_attempts: this.submitAttempts,
      form_errors: this.errors
    };
  }
}

// ============================================
// CAPTURA DE DADOS COMPLETOS
// ============================================

/**
 * Captura todos os dados para envio com o formulário
 */
export function captureAllTrackingData(formTracker?: FormTracker): AllTrackingData {
  const sessionStart = parseInt(sessionStorage.getItem('session_start') || Date.now().toString());
  const timeOnSite = Math.floor((Date.now() - sessionStart) / 1000);
  const pageStart = parseInt(sessionStorage.getItem('current_page_start') || Date.now().toString());
  const timeOnPage = Math.floor((Date.now() - pageStart) / 1000);

  const pagesVisited: PageVisit[] = JSON.parse(sessionStorage.getItem('pages_visited') || '[]');
  const pagesVisitedList = pagesVisited.map(p => p.path);

  const firstVisitDate = localStorage.getItem('first_visit_date');

  return {
    // Dados de Tracking (UTM)
    trackingData: {
      utm_source: sessionStorage.getItem('utm_source') || null,
      utm_medium: sessionStorage.getItem('utm_medium') || null,
      utm_campaign: sessionStorage.getItem('utm_campaign') || null,
      utm_term: sessionStorage.getItem('utm_term') || null,
      utm_content: sessionStorage.getItem('utm_content') || null,
      referrer: sessionStorage.getItem('referrer') || document.referrer || null,
      landing_page: sessionStorage.getItem('landing_page') || null,
      first_touch_point: sessionStorage.getItem('first_touch_point') || null,
      last_cta_clicked: sessionStorage.getItem('last_cta_clicked') || null
    },

    // Dados de Sessão
    sessionData: {
      time_on_site: timeOnSite,
      time_on_page: timeOnPage,
      pages_visited: pagesVisited.length,
      pages_visited_list: pagesVisitedList,
      scroll_depth: getScrollDepth(),
      returning_visitor: localStorage.getItem('visit_count') !== '1',
      previous_visits: parseInt(localStorage.getItem('visit_count') || '1') - 1,
      first_visit_date: firstVisitDate,
      session_id: sessionStorage.getItem('session_id'),
      visitor_id: localStorage.getItem('visitor_id')
    },

    // Dados de Dispositivo
    deviceData: {
      device_type: detectDeviceType(),
      device_os: detectOS(),
      browser: detectBrowser(),
      browser_version: detectBrowserVersion(),
      browser_language: navigator.language || (navigator as any).userLanguage,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },

    // Dados de Formulário
    formData: formTracker ? formTracker.getData() : null,

    // Dados de Consentimento (será preenchido ao aceitar)
    consentData: {
      lgpd_consent: true, // Assumindo que já aceitou na tela anterior
      lgpd_consent_type: 'explicit',
      lgpd_consent_timestamp: new Date().toISOString(),
      privacy_policy_version: 'v2.1',
      marketing_consent: true
    }
  };
}

// ============================================
// DETECÇÃO DE DISPOSITIVO E NAVEGADOR
// ============================================

function detectDeviceType(): string {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

function detectOS(): string {
  const ua = navigator.userAgent;
  if (ua.indexOf('Win') !== -1) return 'Windows';
  if (ua.indexOf('Mac') !== -1) return 'MacOS';
  if (ua.indexOf('Linux') !== -1) return 'Linux';
  if (ua.indexOf('Android') !== -1) return 'Android';
  if (ua.indexOf('iOS') !== -1 || ua.indexOf('iPhone') !== -1 || ua.indexOf('iPad') !== -1) return 'iOS';
  return 'Unknown';
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.indexOf('Firefox') !== -1) return 'Firefox';
  if (ua.indexOf('Opera') !== -1 || ua.indexOf('OPR') !== -1) return 'Opera';
  if (ua.indexOf('Trident') !== -1) return 'Internet Explorer';
  if (ua.indexOf('Edge') !== -1) return 'Edge';
  if (ua.indexOf('Chrome') !== -1) return 'Chrome';
  if (ua.indexOf('Safari') !== -1) return 'Safari';
  return 'Unknown';
}

function detectBrowserVersion(): string {
  const ua = navigator.userAgent;
  const match = ua.match(/(firefox|opera|chrome|safari|trident|edge)\/?\s*(\d+)/i) || [];
  return match[2] || 'Unknown';
}

// ============================================
// UTILIDADES
// ============================================

function generateId(prefix = 'id'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================
// EXPORTAR PARA DEBUG
// ============================================

export function debugTracking(): void {
  console.group('🔍 Analytics Tracker - Debug');
  console.log('Visitor ID:', localStorage.getItem('visitor_id'));
  console.log('Session ID:', sessionStorage.getItem('session_id'));
  console.log('Visit Count:', localStorage.getItem('visit_count'));
  console.log('Pages Visited:', sessionStorage.getItem('pages_visited_count'));
  console.log('Time on Site:', getTimeOnSite() + 's');
  console.log('Scroll Depth:', getScrollDepth() + '%');
  console.log('UTM Source:', sessionStorage.getItem('utm_source'));
  console.log('Landing Page:', sessionStorage.getItem('landing_page'));
  console.groupEnd();
}

// Disponibilizar no console para debug
declare global {
  interface Window {
    debugTracking: typeof debugTracking;
  }
}

if (typeof window !== 'undefined') {
  window.debugTracking = debugTracking;
}
